import React, { useState, useCallback, useEffect, useRef } from 'react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import RegionEditor from './RegionEditor';
import FormatColumnsEditor from './FormatColumnsEditor';
import FormatPromptsEditor from './FormatPromptsEditor';
import FormatMappingsEditor from './FormatMappingsEditor';
import FormatTestPanel from './FormatTestPanel';
import { normalizeRegionsForEditor, normalizePrompts } from './formatBuilderUtils';

const TABS = ['Regions', 'Columns', 'AI Prompts', 'Excel Mapping', 'Samples', 'Test'];

function normalizeFormat(fmt) {
  if (!fmt) return fmt;
  return {
    ...fmt,
    regions: normalizeRegionsForEditor(fmt.regions || []),
    columns: Array.isArray(fmt.columns) ? fmt.columns : [],
    prompts: normalizePrompts(fmt.prompts),
    mappings: Array.isArray(fmt.mappings) ? fmt.mappings : [],
    attachments: Array.isArray(fmt.attachments) ? fmt.attachments : [],
  };
}

const FormatEditor = ({
  formatData,
  canEdit = true,
  canEditLabels = true,
  canEditRegions = true,
  instance,
  accounts,
  onClose,
  routePrefix = '/pdf-extractor/builder',
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [format, setFormat] = useState(() => normalizeFormat(formatData.format));
  const [config] = useState(formatData.config);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  const [editName, setEditName] = useState(format.name);
  const [editDesc, setEditDesc] = useState(format.description || '');
  const [editFirm, setEditFirm] = useState(format.engineering_firm || format.engineeringFirm || '');
  const [editClient, setEditClient] = useState(format.client || '');
  const [attachments, setAttachments] = useState(format.attachments || []);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const formatId = format.id;
  const isV2 = routePrefix.includes('/v2/');
  const isBuiltin = !!format.is_builtin;
  const labelsEditable = canEditLabels || canEdit;
  // Built-ins: regions persist via format overrides (canEditRegions=true even when canEdit=false)
  const regionsEditable = canEditRegions != null ? !!canEditRegions : !!canEdit;

  const showSaveResult = (msg, isError) => {
    setSaveMsg({ text: msg, isError });
    setTimeout(() => setSaveMsg(null), 3000);
  };

  const refreshAttachments = useCallback(async () => {
    if (!isV2 || isBuiltin) return;
    try {
      const res = await authenticatedApiCall(instance, accounts, 'GET', `${routePrefix}/formats/${formatId}/attachments`);
      if (res.data.success) setAttachments(res.data.data.attachments || []);
    } catch { /* ignore */ }
  }, [instance, accounts, routePrefix, formatId, isV2, isBuiltin]);

  useEffect(() => { refreshAttachments(); }, [refreshAttachments]);

  const handleSaveInfo = async () => {
    setSaving(true);
    try {
      if (isBuiltin && isV2) {
        const res = await authenticatedApiCall(instance, accounts, 'PUT', `${routePrefix}/formats/${formatId}/labels`, {
          engineeringFirm: editFirm.trim() || null,
          client: editClient.trim() || null,
        });
        if (res.data.success) {
          setFormat(prev => normalizeFormat({
            ...prev,
            engineering_firm: res.data.data.engineering_firm,
            client: res.data.data.client,
          }));
          showSaveResult('Firm / client saved', false);
        }
      } else {
        const body = { name: editName, description: editDesc };
        if (isV2) {
          body.engineeringFirm = editFirm.trim() || null;
          body.client = editClient.trim() || null;
        }
        const res = await authenticatedApiCall(instance, accounts, 'PUT', `${routePrefix}/formats/${formatId}`, body);
        if (res.data.success) {
          setFormat(prev => normalizeFormat({
            ...prev,
            ...res.data.data.format,
            engineering_firm: res.data.data.format?.engineering_firm ?? editFirm.trim(),
            client: res.data.data.format?.client ?? editClient.trim(),
          }));
          showSaveResult('Format info saved', false);
        }
      }
    } catch { showSaveResult('Failed to save', true); }
    finally { setSaving(false); }
  };

  const handleSaveRegions = useCallback(async (regions) => {
    if (!regionsEditable) {
      showSaveResult('Regions are read-only for this format', true);
      return null;
    }
    setSaving(true);
    try {
      const withIds = normalizeRegionsForEditor(regions);
      const res = await authenticatedApiCall(instance, accounts, 'POST', `${routePrefix}/formats/${formatId}/regions`, { regions: withIds });
      if (res.data.success) {
        const saved = normalizeRegionsForEditor(res.data.data.regions || withIds);
        setFormat(prev => ({ ...prev, regions: saved }));
        showSaveResult('Regions saved', false);
        return saved;
      }
      showSaveResult(res.data.error || 'Failed to save regions', true);
    } catch (err) {
      showSaveResult(err.response?.data?.error || 'Failed to save regions', true);
    } finally { setSaving(false); }
    return null;
  }, [instance, accounts, formatId, routePrefix, regionsEditable, isBuiltin]);

  const handleSaveColumns = useCallback(async (columns) => {
    setSaving(true);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'POST', `${routePrefix}/formats/${formatId}/columns`, { columns });
      if (res.data.success) {
        const saved = Array.isArray(res.data.data.columns) ? res.data.data.columns : columns;
        setFormat(prev => ({ ...prev, columns: saved }));
        showSaveResult('Columns saved', false);
        return saved;
      }
    } catch { showSaveResult('Failed to save columns', true); }
    finally { setSaving(false); }
    return null;
  }, [instance, accounts, formatId, routePrefix]);

  const handleSavePrompts = useCallback(async (promptsToSave) => {
    setSaving(true);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'POST', `${routePrefix}/formats/${formatId}/prompts`, { prompts: promptsToSave });
      if (res.data.success) {
        const saved = normalizePrompts(res.data.data.prompts ?? promptsToSave);
        setFormat(prev => ({ ...prev, prompts: saved }));
        showSaveResult('Prompts saved', false);
        return saved;
      }
    } catch { showSaveResult('Failed to save prompts', true); }
    finally { setSaving(false); }
    return null;
  }, [instance, accounts, formatId, routePrefix]);

  const handleSaveMappings = useCallback(async (mappings) => {
    setSaving(true);
    try {
      const res = await authenticatedApiCall(instance, accounts, 'POST', `${routePrefix}/formats/${formatId}/mappings`, { mappings });
      if (res.data.success) {
        setFormat(prev => ({ ...prev, mappings: res.data.data.mappings }));
        showSaveResult('Mappings saved', false);
        return res.data.data.mappings;
      }
    } catch { showSaveResult('Failed to save mappings', true); }
    finally { setSaving(false); }
    return null;
  }, [instance, accounts, formatId, routePrefix]);

  const handleUploadSample = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authenticatedApiCall(instance, accounts, 'POST', `${routePrefix}/formats/${formatId}/attachments`, fd);
      if (res.data.success) {
        await refreshAttachments();
        showSaveResult('Sample attached', false);
      }
    } catch (err) {
      showSaveResult(err.response?.data?.error || 'Upload failed', true);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownloadSample = async (att) => {
    try {
      const res = await authenticatedApiCall(
        instance, accounts, 'GET',
        `${routePrefix}/formats/${formatId}/attachments/${att.id}`,
        null,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = att.original_name || 'sample';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { showSaveResult('Download failed', true); }
  };

  const handleRemoveSample = async (att) => {
    if (!window.confirm(`Remove sample "${att.original_name}"?`)) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `${routePrefix}/formats/${formatId}/attachments/${att.id}`);
      await refreshAttachments();
      showSaveResult('Sample removed', false);
    } catch (err) {
      showSaveResult(err.response?.data?.error || 'Remove failed', true);
    }
  };

  const regions = normalizeRegionsForEditor(format.regions || []);
  const columns = Array.isArray(format.columns) ? format.columns : [];
  const prompts = normalizePrompts(format.prompts);
  const mappings = Array.isArray(format.mappings) ? format.mappings : [];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={onClose} style={backBtn}>&larr; Back</button>
          <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>{canEdit ? 'Editing' : 'Viewing'}: {format.name}</h2>
          {!canEdit && format.is_locked && (
            <span title="A superadmin must unlock this format before changes" style={{ fontSize: 12, color: '#664d03', fontWeight: 600, padding: '2px 8px', borderRadius: 10, backgroundColor: '#fff3cd', border: '1px solid #ffe69c' }}>&#128274; Locked (read-only)</span>
          )}
          {isBuiltin && (
            <span title="Hardcoded system format — use Copy on the format list to create an editable version" style={{ fontSize: 12, color: '#084298', fontWeight: 500, padding: '2px 8px', borderRadius: 10, backgroundColor: '#cfe2ff', border: '1px solid #9ec5fe' }}>Built-in (read-only)</span>
          )}
          {!canEdit && !format.is_locked && !isBuiltin && <span style={{ fontSize: 12, color: '#dc3545', fontWeight: 500 }}>Read-only</span>}
          {saving && <span style={{ fontSize: 12, color: '#6c757d' }}>Saving...</span>}
          {saveMsg && <span style={{ fontSize: 12, color: saveMsg.isError ? '#dc3545' : '#198754' }}>{saveMsg.text}</span>}
        </div>
      </div>

      {isBuiltin && (
        <div style={{ marginBottom: 16, padding: '10px 14px', backgroundColor: '#e7f1ff', border: '1px solid #b6d4fe', borderRadius: 6, fontSize: 13, color: '#084298', lineHeight: 1.45 }}>
          This format is <strong>hardcoded</strong> and cannot be changed here. Go back to the list and click <strong>Copy</strong> to create an editable custom format, then edit regions / columns / prompts on the copy.
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 160 }}>
          <label style={labelStyle}>Name</label>
          <input value={editName} onChange={e => setEditName(e.target.value)} style={inputStyle} disabled={!canEdit} />
        </div>
        {isV2 && (
          <div style={{ flex: 1, minWidth: 140 }}>
            <label style={labelStyle}>Engineering Firm</label>
            <input value={editFirm} onChange={e => setEditFirm(e.target.value)} style={inputStyle} disabled={!labelsEditable} placeholder="Type firm name" />
          </div>
        )}
        {isV2 && (
          <div style={{ flex: 1, minWidth: 140 }}>
            <label style={labelStyle}>Client</label>
            <input value={editClient} onChange={e => setEditClient(e.target.value)} style={inputStyle} disabled={!labelsEditable} placeholder="Type client name" />
          </div>
        )}
        <div style={{ flex: 2, minWidth: 180 }}>
          <label style={labelStyle}>Description</label>
          <input value={editDesc} onChange={e => setEditDesc(e.target.value)} style={inputStyle} disabled={!canEdit} />
        </div>
        {(canEdit || labelsEditable) && (
          <div style={{ alignSelf: 'flex-end' }}>
            <button onClick={handleSaveInfo} disabled={saving || (!isBuiltin && !editName.trim())} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
              Save Info
            </button>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', borderBottom: '2px solid #dee2e6', marginBottom: 0, flexWrap: 'wrap' }}>
        {TABS.map((tab, i) => (
          <button
            key={tab}
            onClick={() => setActiveTab(i)}
            style={{
              padding: '10px 20px', fontSize: 14, fontWeight: 500, cursor: 'pointer',
              border: 'none', borderBottom: activeTab === i ? '2px solid #0d6efd' : '2px solid transparent',
              backgroundColor: 'transparent', color: activeTab === i ? '#0d6efd' : '#6c757d',
              marginBottom: -2,
            }}
          >{tab}{tab === 'Samples' && attachments.length > 0 ? ` (${attachments.length})` : ''}</button>
        ))}
      </div>

      <div style={{ padding: '20px 0' }}>
        {activeTab === 0 && (
          <RegionEditor
            formatId={formatId}
            regions={regions}
            onSave={handleSaveRegions}
            readOnly={!regionsEditable}
            instance={instance}
            accounts={accounts}
            pdfToImageEndpoint={routePrefix.includes('/v2/') ? '/pdf-extractor/v2/pdf-to-image' : '/pdf-extractor/builder/pdf-to-image'}
          />
        )}
        {activeTab === 1 && (
          <FormatColumnsEditor
            columns={columns}
            regions={regions}
            onSave={handleSaveColumns}
          />
        )}
        {activeTab === 2 && (
          <FormatPromptsEditor
            prompts={prompts}
            regions={regions}
            columns={columns}
            onSave={handleSavePrompts}
          />
        )}
        {activeTab === 3 && (
          <FormatMappingsEditor
            mappings={mappings}
            columns={columns}
            onSave={handleSaveMappings}
          />
        )}
        {activeTab === 4 && (
          <div>
            <p style={{ fontSize: 13, color: '#6c757d', marginTop: 0, marginBottom: 12 }}>
              Attach sample drawings used to build this format (PDF or images). Multiple files allowed.
            </p>
            {isBuiltin || !isV2 ? (
              <div style={{ padding: 16, backgroundColor: '#f8f9fa', borderRadius: 6, fontSize: 13, color: '#6c757d' }}>
                Sample attachments are only available for custom V2 formats.
              </div>
            ) : (
              <>
                {canEdit && (
                  <div style={{ marginBottom: 12 }}>
                    <input ref={fileInputRef} type="file" accept=".pdf,image/png,image/jpeg" onChange={handleUploadSample} style={{ display: 'none' }} />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      style={{ ...btnPrimary, opacity: uploading ? 0.6 : 1 }}
                    >
                      {uploading ? 'Uploading...' : '+ Add Sample'}
                    </button>
                  </div>
                )}
                {attachments.length === 0 ? (
                  <div style={{ padding: 16, backgroundColor: '#f8f9fa', borderRadius: 6, fontSize: 13, color: '#6c757d' }}>
                    No sample drawings attached yet.
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e9ecef', borderRadius: 6 }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa' }}>
                        <th style={thStyle}>File</th>
                        <th style={thStyle}>Size</th>
                        <th style={thStyle}>Uploaded</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attachments.map((att) => (
                        <tr key={att.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={tdStyle}>{att.original_name}</td>
                          <td style={tdStyle}>{att.size_bytes ? `${(att.size_bytes / 1024).toFixed(1)} KB` : '—'}</td>
                          <td style={tdStyle}>
                            <span style={{ fontSize: 12, color: '#6c757d' }}>
                              {att.uploaded_by ? att.uploaded_by.split('@')[0] : '—'}
                              {att.created_at ? ` · ${new Date(att.created_at).toLocaleDateString()}` : ''}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            <button onClick={() => handleDownloadSample(att)} style={actionBtn}>Download</button>
                            {canEdit && (
                              <button onClick={() => handleRemoveSample(att)} style={{ ...actionBtn, color: '#dc3545', marginLeft: 4 }}>Remove</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        )}
        {activeTab === 5 && (
          <FormatTestPanel
            formatId={formatId}
            config={config}
            instance={instance}
            accounts={accounts}
          />
        )}
      </div>
    </div>
  );
};

const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 14, fontFamily: 'inherit' };
const labelStyle = { display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 500, color: '#495057' };
const btnPrimary = { padding: '8px 16px', backgroundColor: '#0d6efd', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const backBtn = { padding: '6px 14px', fontSize: 13, border: '1px solid #ced4da', borderRadius: 6, backgroundColor: '#fff', cursor: 'pointer', color: '#495057' };
const thStyle = { padding: '7px 10px', fontSize: 12, fontWeight: 600, color: '#495057', borderBottom: '2px solid #dee2e6', textAlign: 'left' };
const tdStyle = { padding: '6px 10px', fontSize: 13 };
const actionBtn = { padding: '3px 8px', fontSize: 11, border: '1px solid #ced4da', borderRadius: 4, backgroundColor: '#fff', cursor: 'pointer', color: '#495057' };

export default FormatEditor;